import { Tab, useTheme } from '@material-ui/core';
import { Backdrop } from '@room-match/shared-ui-components';
import React, { useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import { CONFIG } from 'shared/config';
import { ROUTES } from 'shared/constants/ROUTES';
import useWindowDimensions from 'shared/hooks/useWindowDimenstion';
import { profileService } from 'shared/services/profileService';
import { ActiveTabContainer, EditProfileCont, TabContainer, TabHeader } from './EditProfile.styles';
import SideInfo from './SideInfo';
import CompCard from './Tabs/CompositeCard/CompositeCard';
import ImagesVideoAudio from './Tabs/ImagesVideoAudio/ImagesVideoAudio';
import Resume from './Tabs/Resume/Resume';
import CompositeCard from './Tabs/CompositeCard/CompositeCard';
import { IProfileGraphQL } from 'shared/interfaces/IProfile';

const { getSingleProfileGraphQL } = profileService();

interface Props {
  profiles?: IProfileGraphQL[];
}

const EditProfile: React.FC<Props> = ({ profiles }) => {
  const savedTheme = useTheme().palette.type;
  const { tab, profileId } = useParams() as { tab: string; profileId: string };
  const { data, isLoading } = getSingleProfileGraphQL(profileId);
  const profile = useMemo(() => {
    if (!isLoading && data) return data.data.profile;
  }, [data, isLoading]);
  const queryClient = useQueryClient();
  const history = useHistory();
  const { width } = useWindowDimensions();
  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    history.push(`${ROUTES.TALENT.PROFILES}/${profileId}/${newValue}`);
  };

  // Use the global permission if any the condition is met
  const use_global_permission =
    // If use own permission is false
    !profile?.use_own_talent_permissions ||
    // if the individual talent permission is null or undefined
    profile?.talent_permissions === null ||
    profile?.talent_permissions === undefined;

  // Permission
  const permissions: string[] = useMemo(
    () => (use_global_permission ? profile?.agency?.talent_permissions : profile?.talent_permissions) || [],
    [use_global_permission, isLoading, data],
  );

  // useMemoTabs
  const tabs = useMemo(() => {
    return [
      {
        name: 'images_video_audio',
        header: 'Media/Documents',
        component: (
          <ImagesVideoAudio
            profiles={profiles || []}
            permissions={permissions}
            nonRepre={profile?.representation_type === 'non_represented'}
          />
        ),
        disabled: false,
      },
      {
        name: 'resume',
        header: 'Resume',
        component: <Resume permissions={permissions} />,
        disabled: false,
      },
      {
        name: 'composite_card',
        header: 'Composite Card',
        component: (
          <CompositeCard permissions={permissions} nonRepre={profile?.representation_type === 'non_represented'} />
        ),
        disabled: false,
      },
    ];
  }, [profile]);

  const getActiveTab = useMemo(() => {
    return tabs.find((_tab) => _tab.name === tab)?.component;
  }, [tab, tabs]);

  if (isLoading) return <Backdrop />;

  return (
    <EditProfileCont>
      {['images_video_audio', 'calendar_events'].indexOf(tab) > -1 && profile ? (
        <SideInfo data={profile} permissions={permissions} />
      ) : (
        tab === 'resume' && width > 960 && profile && <SideInfo data={profile} permissions={permissions} />
      )}
      <TabContainer>
        <TabHeader
          value={tab || 'images_video_audio'}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs //filter compcard if no permission on Composite Card
            .filter((tab) => {
              if (tab.name === 'composite_card') {
                return permissions?.includes('Composite Card') || profile?.representation_type === 'non_represented';
              }
              return true;
            })
            .map((tab, index) => (
              <Tab
                onClick={() => {}}
                key={index}
                label={tab.header}
                disabled={tab.disabled}
                value={tab.name}
                style={{ textTransform: 'capitalize' }}
              />
            ))}
        </TabHeader>
        <ActiveTabContainer>
          {getActiveTab || (
            <ImagesVideoAudio
              profiles={profiles || []}
              permissions={permissions || []}
              nonRepre={profile?.representation_type === 'non_represented'}
            />
          )}
        </ActiveTabContainer>
      </TabContainer>
    </EditProfileCont>
  );
};

export default EditProfile;
